import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedPhoneNumber from 'I18n/components/FormattedPhoneNumber';
import { ResultsMessage } from 'foundations-components/transitional/components/ResultsMessage';
import { H4 } from 'foundations-components/transitional/components/H4';
import { BreakString } from 'foundations-components/transitional/components/BreakString';
import { View } from 'foundations-components/transitional/components/View';
import { CANCELED, FAILED, PENDING, SOURCE_ID_PAID, SUBMITTED, SUCCEEDED } from '../../constants/Statuses';
import { getIsZeroCheckoutValid } from '../../utils/validations';
import { ACH, BACS, PADS, SEPA } from '../../constants/PaymentMethods';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import { EmptyStateError } from '../Utilities/NetworkErrorPage';
import { jsx as _jsx } from "react/jsx-runtime";
const PaymentStatus = ({
  checkoutSession
}) => {
  var _checkoutSession$paym;
  const {
    formState
  } = usePaymentFormContext();
  const status = checkoutSession.status;
  const paymentMethodType = (_checkoutSession$paym = checkoutSession.paymentDetails) === null || _checkoutSession$paym === void 0 || (_checkoutSession$paym = _checkoutSession$paym.paymentMethod) === null || _checkoutSession$paym === void 0 ? void 0 : _checkoutSession$paym.type;
  const isBankTransfer = paymentMethodType === ACH || paymentMethodType === SEPA || paymentMethodType === BACS || paymentMethodType === PADS;

  // email is set after a the submit goes through
  // git.hubteam.com/HubSpotProtected/payments-ui/blob/master/payments-ui-app/static-1.27283/js/hooks/submitPayment.tsx#L698
  const customerEmail = formState.get('email');
  const isZeroCheckout = getIsZeroCheckoutValid(checkoutSession.subtotalsPublicResponse.paymentAmount, checkoutSession.lineItems);
  if (status === SOURCE_ID_PAID) {
    return /*#__PURE__*/_jsx(ResultsMessage, {
      marginBlockStart: "0",
      "data-test-id": "payment-status-source-id-paid",
      illustration: "payments-button",
      illustrationProps: {
        height: 175,
        alt: 'bank with green check mark and credit card'
      },
      title: /*#__PURE__*/_jsx(H4, {
        marginBlockStart: "300",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.paymentStatus.sourceIdPaid.header"
        })
      }),
      children: /*#__PURE__*/_jsx(BreakString, {
        hyphenate: false,
        children: /*#__PURE__*/_jsx(View, {
          marginInline: "auto",
          marginBlockStart: "-400",
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "payments.paymentStatus.sourceIdPaid.body_jsx",
            options: {
              email: checkoutSession.companyDetails.supportEmail,
              phone: checkoutSession.companyDetails.supportPhone
            },
            elements: {
              Phone: FormattedPhoneNumber
            }
          })
        })
      })
    });
  }
  if (status === CANCELED) {
    return /*#__PURE__*/_jsx(EmptyStateError, {});
  }
  if (!paymentMethodType) {
    return null;
  }
  if (status === PENDING) {
    const pendingBodyMessageKey = (() => {
      switch (paymentMethodType) {
        case ACH:
          return 'payments.paymentStatus.pending.body.ach';
        case BACS:
          return 'payments.paymentStatus.pending.body.bacs';
        case PADS:
          return 'payments.paymentStatus.pending.body.pads';
        case SEPA:
          return 'payments.paymentStatus.pending.body.sepa';
        default:
          return 'payments.paymentStatus.pending.body.card';
      }
    })();
    return /*#__PURE__*/_jsx(ResultsMessage, {
      "data-test-id": "payment-status-pending",
      marginBlockStart: "0",
      illustration: isBankTransfer ? 'success-green' : 'errors/hourglass',
      illustrationProps: {
        width: 150,
        height: 165,
        alt: isBankTransfer ? 'green checkmark' : 'hourglass'
      },
      title: /*#__PURE__*/_jsx(H4, {
        marginBlockStart: "300",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.paymentStatus.pending.header"
        })
      }),
      children: !!customerEmail && /*#__PURE__*/_jsx(BreakString, {
        hyphenate: false,
        children: /*#__PURE__*/_jsx(View, {
          inlineSize: "75%",
          marginInline: "auto",
          marginBlockStart: "-400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            "data-vr-hidden": "true",
            message: pendingBodyMessageKey,
            options: {
              customerEmail
            }
          })
        })
      })
    });
  }
  if (status === SUBMITTED || status === SUCCEEDED) {
    const successHeaderMessageKey = isZeroCheckout ? 'payments.paymentStatus.success.scheduled' : 'payments.paymentStatus.success.header';
    return /*#__PURE__*/_jsx(ResultsMessage, {
      "data-test-id": "payment-status-succeeded",
      illustration: "success-green",
      marginBlockStart: "0",
      illustrationProps: {
        height: 165,
        alt: 'green checkmark'
      },
      title: /*#__PURE__*/_jsx(H4, {
        marginBlockStart: "300",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: successHeaderMessageKey
        })
      }),
      children: !!customerEmail && /*#__PURE__*/_jsx(BreakString, {
        hyphenate: false,
        children: /*#__PURE__*/_jsx(View, {
          inlineSize: "75%",
          marginInline: "auto",
          marginBlockStart: "-400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            "data-vr-hidden": "true",
            message: "payments.paymentStatus.success.body",
            options: {
              customerEmail
            }
          })
        })
      })
    });
  }
  if (status === FAILED) {
    return /*#__PURE__*/_jsx(ResultsMessage, {
      marginBlockStart: "0",
      "data-test-id": "payment-status-failed",
      illustration: "carts",
      illustrationProps: {
        height: 138,
        width: 150,
        alt: 'greyscale empty shopping bag'
      },
      title: /*#__PURE__*/_jsx(H4, {
        marginBlockStart: "300",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.paymentStatus.failure.header"
        })
      }),
      children: /*#__PURE__*/_jsx(BreakString, {
        hyphenate: false,
        children: /*#__PURE__*/_jsx(View, {
          inlineSize: "75%",
          marginInline: "auto",
          marginBlockStart: "-400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.paymentStatus.failure.body",
            options: {
              supportEmail: checkoutSession.companyDetails.supportEmail,
              supportPhone: checkoutSession.companyDetails.supportPhone
            }
          })
        })
      })
    });
  }
  return null;
};
export default PaymentStatus;